import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { concatMap, map, reduce, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import type { ApiEnvelope } from './super-admin-auth.service';
import { assertSuccessEnvelope } from '../http/api-envelope.operators';
import type { CatalogRow } from 'src/app/shared/utils/tenant-onboarding-catalog';
export type { CatalogRow } from 'src/app/shared/utils/tenant-onboarding-catalog';

export interface TenantListMeta {
  total: number;
  lastPage: number;
  currentPage: number;
  perPage: number;
  prev: number | null;
  next: number | null;
}

/** Row from paginated `GET /tenant`. */
export interface TenantRow {
  id: number;
  uuid: string;
  unique_id: string;
  tenant_name: string | null;
  tenant_subdomain: string | null;
  email: string | null;
  logo: string | null;
  contact_number: string | null;
  /** E.164 country calling code digits or `+`-prefixed (e.g. `91`, `+1`). */
  contact_number_country_code?: string | null;
  /** ISO 3166-1 alpha-2 — optional disambiguation for shared dial codes. */
  contact_number_country_iso?: string | null;
  contact_person: string | null;
  status: string;
  /** PREPAID clients buy a plan up front; POSTPAID are invoiced for usage in arrears. */
  billing_type?: string | null;
  created_at: string;
  updated_at?: string;
}

/** Soft-deleted row from `GET /tenant/deleted` — same columns + audit fields. */
export interface TenantDeletedRow extends TenantRow {
  deleted_at?: string | null;
  deleted_by?: number | null;
  deletedBy?: { id: number; full_name: string | null } | null;
}

export interface TenantListPayload {
  result: TenantRow[];
  meta: TenantListMeta;
}

export interface TenantDeletedListPayload {
  result: TenantDeletedRow[];
  meta: TenantListMeta;
}

/** `GET /tenant/:id` — tenant detail for edit forms */
export interface TenantCommercialProfileDto {
  amount_received?: unknown;
  date_of_receipt?: string | null;
  un_number?: string | null;
  home_loan_pd_charges?: unknown;
  personal_loan_pd_charges?: unknown;
  metadata?: unknown;
}

export interface TenantDetailPayload {
  id: number;
  uuid?: string;
  unique_id?: string;
  tenant_name: string | null;
  tenant_subdomain: string | null;
  email: string | null;
  /** Legacy field; UI no longer edits industry */
  industry?: string | null;
  currency?: string | null;
  currency_symbol?: string | null;
  timezone?: string | null;
  status?: string;
  logo?: string | null;
  /** PAN card JSON; `preview_url` is added by API for private S3. */
  profile_picture?: {
    storage_path?: string;
    preview_url?: string | null;
    stored_file_name?: string;
    original_file_name?: string;
    mime_type?: string;
    size?: number;
  } | null;
  contact_number?: string | null;
  contact_number_country_code?: string | null;
  /** ISO 3166-1 alpha-2 — disambiguates shared dial codes (e.g. +1 → US vs CA). */
  contact_number_country_iso?: string | null;
  contact_person?: string | null;
  contact_person_phone?: string | null;
  contact_person_phone_country_code?: string | null;
  contact_person_phone_country_iso?: string | null;
  gstn?: string | null;
  business_pan?: string | null;
  billing_address?: string | null;
  billing_city?: string | null;
  billing_pincode?: string | null;
  commercial_profile?: TenantCommercialProfileDto | null;
  /** From `tenant_products` / `tenant_services` junction tables (authoritative when present). */
  selected_product_slugs?: string[];
  selected_service_slugs?: string[];
  billing_type?: 'PREPAID' | 'POSTPAID';
  billing_frequency?: 'DAILY' | 'WEEKLY' | 'MONTHLY' | null;
  /** `{ product_slug: charge_per_pd }` — only populated for postpaid tenants. */
  product_charges?: Record<string, number>;
}

export interface TenantListQuery {
  page: number;
  page_size: number;
  searchText?: string;
  sortColumn?: string;
  sortBy?: 'asc' | 'desc';
  status?: string;
  createdFrom?: string;
  createdTo?: string;
  /** Only tenants with this product in `tenant_products`. */
  product_id?: number;
}

function toHttpParams(q: TenantListQuery): HttpParams {
  let p = new HttpParams()
    .set('page', String(q.page))
    .set('page_size', String(q.page_size));
  if (q.searchText?.trim()) {
    p = p.set('searchText', q.searchText.trim());
  }
  if (q.sortColumn) {
    p = p.set('sortColumn', q.sortColumn);
  }
  if (q.sortBy) {
    p = p.set('sortBy', q.sortBy);
  }
  if (q.status?.trim()) {
    p = p.set('status', q.status.trim());
  }
  if (q.createdFrom?.trim()) {
    p = p.set('createdFrom', q.createdFrom.trim());
  }
  if (q.createdTo?.trim()) {
    p = p.set('createdTo', q.createdTo.trim());
  }
  if (q.product_id != null && q.product_id > 0) {
    p = p.set('product_id', String(q.product_id));
  }
  return p;
}

/** API key auto-issued during onboarding — the raw key is returned exactly once. */
export interface IssuedApiKey {
  key: string;
  prefix: string;
  environment: string;
  scopes: string[];
}

/** `POST /tenant` response payload (the default API key, when issuance succeeded). */
export interface CreatedTenantPayload {
  api_key?: IssuedApiKey;
}

@Injectable({ providedIn: 'root' })
export class TenantOnboardingApiService {
  private readonly base = environment.apiBaseUrl.replace(/\/$/, '');

  constructor(private readonly http: HttpClient) {}

  /** Paginated tenant list (`GET /tenant`). Requires JWT cookie. */
  listTenants(query: TenantListQuery): Observable<ApiEnvelope<TenantListPayload>> {
    return this.http.get<ApiEnvelope<TenantListPayload>>(`${this.base}/tenant`, {
      params: toHttpParams(query),
    });
  }

  /** Paginated soft-deleted tenants (`GET /tenant/deleted`). Same query shape as {@link listTenants}. */
  listDeletedTenants(query: TenantListQuery): Observable<ApiEnvelope<TenantDeletedListPayload>> {
    return this.http.get<ApiEnvelope<TenantDeletedListPayload>>(`${this.base}/tenant/deleted`, {
      params: toHttpParams(query),
    });
  }

  /**
   * Fetches every page for the given filter/sort (for export). Uses sequential requests to avoid overloading the API.
   * Pass a large `page_size` in `filter` (e.g. 500) to minimize round-trips.
   */
  fetchAllTenantRows(
    filter: Omit<TenantListQuery, 'page'>,
    deleted: boolean,
  ): Observable<Array<TenantRow | TenantDeletedRow>> {
    const listFn = deleted
      ? (q: TenantListQuery) => this.listDeletedTenants(q)
      : (q: TenantListQuery) => this.listTenants(q);

    return listFn({ ...filter, page: 1 }).pipe(
      switchMap((first) => {
        const envelope = first?.data;
        if (!envelope?.meta) {
          return of([]);
        }
        const rows = (envelope.result ?? []) as Array<TenantRow | TenantDeletedRow>;
        const lastPage = envelope.meta.lastPage;
        if (lastPage <= 1) {
          return of(rows);
        }
        const restPageNums = Array.from({ length: lastPage - 1 }, (_, i) => i + 2);
        return from(restPageNums).pipe(
          concatMap((page) =>
            listFn({ ...filter, page }).pipe(
              map((r) => (r.data?.result ?? []) as Array<TenantRow | TenantDeletedRow>),
            ),
          ),
          reduce((acc, chunk) => acc.concat(chunk), rows),
        );
      }),
    );
  }

  /** Restore soft-deleted tenant (`PATCH /tenant/restore/:id`). */
  restoreTenant(id: number): Observable<ApiEnvelope<unknown>> {
    return this.http.patch<ApiEnvelope<unknown>>(`${this.base}/tenant/restore/${id}`, {});
  }

  /** Permanent delete — drops tenant DB (`DELETE /tenant/permanent-delete/:id`). */
  permanentDeleteTenant(id: number): Observable<ApiEnvelope<unknown>> {
    return this.http.delete<ApiEnvelope<unknown>>(`${this.base}/tenant/permanent-delete/${id}`);
  }

  /** Tenant by id (`GET /tenant/:id`). Requires JWT cookie. */
  getTenantById(id: number): Observable<ApiEnvelope<TenantDetailPayload>> {
    return this.http.get<ApiEnvelope<TenantDetailPayload>>(`${this.base}/tenant/${id}`);
  }

  /** Tenant by UUID (`GET /tenant/by-uuid/:uuid`). Requires JWT cookie. */
  getTenantByUuid(uuid: string): Observable<ApiEnvelope<TenantDetailPayload>> {
    const u = encodeURIComponent(uuid.trim());
    return this.http.get<ApiEnvelope<TenantDetailPayload>>(
      `${this.base}/tenant/by-uuid/${u}`,
    );
  }

  /** Multipart tenant update (`PATCH /tenant/:id`). Requires JWT cookie. */
  updateTenant(id: number, formData: FormData): Observable<ApiEnvelope<unknown>> {
    return this.http
      .patch<ApiEnvelope<unknown>>(`${this.base}/tenant/${id}`, formData)
      .pipe(assertSuccessEnvelope());
  }

  /** Soft-delete tenant (`DELETE /tenant/:id`). Requires JWT cookie. */
  deleteTenant(id: number): Observable<ApiEnvelope<unknown>> {
    return this.http.delete<ApiEnvelope<unknown>>(`${this.base}/tenant/${id}`);
  }

  /**
   * Toggle tenant Active/InActive (`PATCH /tenant/toggle/status/:id`).
   * Body: `{ status: 'Active' | 'InActive' | ... }` — the target status.
   */
  toggleTenantStatus(
    id: number,
    status: 'Active' | 'InActive' | 'Blocked' | 'Suspended' | 'Locked',
  ): Observable<ApiEnvelope<{ id: number; status: string }>> {
    return this.http.patch<ApiEnvelope<{ id: number; status: string }>>(
      `${this.base}/tenant/toggle/status/${id}`,
      { status },
    );
  }

  /** Apply pending Prisma migrations to tenant DB schemas (`POST /tenant/apply-migration`). Body: `{ tenant_id }`. */
  applyTenantMigration(tenantId: number): Observable<ApiEnvelope<unknown>> {
    return this.http
      .post<ApiEnvelope<unknown>>(`${this.base}/tenant/apply-migration`, {
        tenant_id: tenantId,
      })
      .pipe(assertSuccessEnvelope());
  }

  /** Run tenant reference seed / bootstrap hooks (`POST /tenant/apply-seeder`). Body: `{ tenant_id }`. */
  applyTenantSeeder(tenantId: number): Observable<ApiEnvelope<unknown>> {
    return this.http
      .post<ApiEnvelope<unknown>>(`${this.base}/tenant/apply-seeder`, {
        tenant_id: tenantId,
      })
      .pipe(assertSuccessEnvelope());
  }

  /** Resend tenant welcome / reset link email (`POST /tenant/resend-password-link`). Body: `{ tenant_id }`. */
  resendTenantPasswordLink(tenantId: number): Observable<ApiEnvelope<unknown>> {
    return this.http
      .post<ApiEnvelope<unknown>>(`${this.base}/tenant/resend-password-link`, {
        tenant_id: tenantId,
      })
      .pipe(assertSuccessEnvelope());
  }

  /** Remove tenant logo only (`DELETE /tenant/logo/:id`). Requires JWT cookie. */
  removeTenantLogo(id: number): Observable<ApiEnvelope<unknown>> {
    return this.http.delete<ApiEnvelope<unknown>>(`${this.base}/tenant/logo/${id}`);
  }

  /** Remove PAN / profile picture (`DELETE /tenant/profile-picture/:id`). Requires JWT cookie. */
  removeTenantProfilePicture(id: number): Observable<ApiEnvelope<unknown>> {
    return this.http.delete<ApiEnvelope<unknown>>(
      `${this.base}/tenant/profile-picture/${id}`,
    );
  }

  /** Multipart tenant create — field names must match CreateTenantDto (`logo` optional file). */
  createTenant(formData: FormData): Observable<ApiEnvelope<CreatedTenantPayload>> {
    return this.http
      .post<ApiEnvelope<CreatedTenantPayload>>(`${this.base}/tenant`, formData)
      .pipe(assertSuccessEnvelope());
  }

  /** Active products for onboarding checkboxes (`GET /product/dropdown`). Requires JWT cookie. */
  listProductsDropdown(): Observable<ApiEnvelope<CatalogRow[]>> {
    return this.http.get<ApiEnvelope<CatalogRow[]>>(`${this.base}/product/dropdown`);
  }

  /**
   * Same data as {@link listProductsDropdown} — `GET /product/types/dropdown` (product **types** for tenant onboarding).
   * Requires JWT cookie.
   */
  listProductTypesDropdown(): Observable<ApiEnvelope<CatalogRow[]>> {
    return this.http.get<ApiEnvelope<CatalogRow[]>>(`${this.base}/product/types/dropdown`);
  }

  /** Active loan services for onboarding checkboxes (`GET /services/dropdown`). Requires JWT cookie. */
  listServicesDropdown(): Observable<ApiEnvelope<CatalogRow[]>> {
    return this.http.get<ApiEnvelope<CatalogRow[]>>(`${this.base}/services/dropdown`);
  }

  /** Active tenants for select UIs (`GET /tenant/dropdown`). */
  listTenantsDropdown(): Observable<
    ApiEnvelope<Array<{ id: number; tenant_name: string | null; tenant_subdomain: string | null }>>
  > {
    return this.http.get<
      ApiEnvelope<Array<{ id: number; tenant_name: string | null; tenant_subdomain: string | null }>>
    >(`${this.base}/tenant/dropdown`);
  }
}
